import { createClient, type Session, type SupabaseClient, type User as SupabaseUser } from '@supabase/supabase-js';

// Singleton Supabase client. Browser-side reads VITE_-prefixed env at build
// time. If env is missing the client is null and calling code falls back to
// local-only behavior (handy for tests / offline dev).

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !key) {
  console.warn(
    '[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set — avatar persistence + auth will fall back to local-only mode.',
  );
}

// Pre-warm DNS + TLS to the Supabase origin so the first booth / venue /
// room_state queries don't pay handshake latency on cold visits. Injected
// from JS rather than HTML because the URL is a build-time env var. Cheap
// no-op when env isn't configured or we're not in a browser.
if (url && typeof document !== 'undefined') {
  try {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = url;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  } catch { /* DOM may not be ready in some test envs */ }
}

export const supabase: SupabaseClient | null =
  url && key
    ? createClient(url, key, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: true,
          storageKey: 'boothfm.auth',
        },
      })
    : null;

export const AVATAR_BUCKET = 'avatars';
export const AVATAR_TABLE = 'avatars';
export const TRACK_BUCKET = 'tracks';

// ----------------------------------------------------------------- auth API

export type AuthUser = SupabaseUser;
export type AuthSession = Session;
export type AuthProvider = 'x' | 'google';

/** True when a Supabase client is configured. Use to short-circuit auth UI
 *  in local-only dev mode. */
export function isAuthConfigured(): boolean {
  return supabase !== null;
}

/** Kick off OAuth sign-in. The browser is redirected to the provider; on
 *  return Supabase parses the session out of the URL (detectSessionInUrl)
 *  and fires SIGNED_IN. `redirectTo` defaults to the current page minus
 *  any hash. It must be in the project's allowed Redirect URLs in the
 *  Supabase dashboard (Authentication → URL Configuration). */
export async function signInWithProvider(
  provider: AuthProvider,
  redirectTo?: string,
): Promise<void> {
  if (!supabase) throw new Error('supabase_not_configured');
  const target = redirectTo ?? currentUrlNoHash();
  // Force the account picker for Google. Without prompt=select_account
  // Google silently re-uses the last signed-in account, so after a
  // sign-out the user can't easily switch identities. X has no
  // equivalent param (its OAuth flow already routes through their
  // account selector by default).
  const queryParams = provider === 'google' ? { prompt: 'select_account' } : undefined;
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: { redirectTo: target, queryParams },
  });
  if (error) throw error;
}

function currentUrlNoHash(): string {
  try {
    const u = new URL(window.location.href);
    u.hash = '';
    return u.toString();
  } catch {
    return window.location.href;
  }
}

/** Restore a session at boot from the persisted JWT (or from a fresh OAuth
 *  callback URL — Supabase consumes the hash itself). Returns null when
 *  there's nothing to restore. */
export async function restoreSession(): Promise<AuthSession | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

export async function signOut(): Promise<void> {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export async function getSession(): Promise<AuthSession | null> {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}

/** Subscribe to auth state changes. Fires on SIGNED_IN (incl. OAuth
 *  callback), SIGNED_OUT, TOKEN_REFRESHED, USER_UPDATED. Returns an
 *  unsubscribe function. */
export function onAuthChange(
  cb: (session: AuthSession | null) => void,
): () => void {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange((_event, session) => cb(session));
  return () => data.subscription.unsubscribe();
}
